import { type FC } from 'react';
import i18n from 'i18next';
import { type PackDistribution } from '@starly/starly-types';

import CommonPack from 'static/pack/common/common-pack-no-background.png';
import CommonPack2x from 'static/pack/common/common-pack-no-background@2x.png';
import CommonPack3x from 'static/pack/common/common-pack-no-background@3x.png';
import CommonPackBackground from 'static/pack/common/common-pack-on-background.png';
import CommonPackBackground2x from 'static/pack/common/common-pack-on-background@2x.png';
import CommonPackBackground3x from 'static/pack/common/common-pack-on-background@3x.png';

import RarePackBack from 'static/pack/rare/rare-pack-no-background.png';
import RarePackBack2x from 'static/pack/rare/rare-pack-no-background@2x.png';
import RarePackBack3x from 'static/pack/rare/rare-pack-no-background@3x.png';
import RarePackBackground from 'static/pack/rare/rare-pack-on-background.png';
import RarePackBackground2x from 'static/pack/rare/rare-pack-on-background@2x.png';
import RarePackBackground3x from 'static/pack/rare/rare-pack-on-background@3x.png';

import LegendaryPack from 'static/pack/legendary/legendary-pack-no-background.png';
import LegendaryPack2x from 'static/pack/legendary/legendary-pack-no-background@2x.png';
import LegendaryPack3x from 'static/pack/legendary/legendary-pack-no-background@3x.png';
import LegendaryPackBackground from 'static/pack/legendary/legendary-pack-on-background.png';
import LegendaryPackBackground2x from 'static/pack/legendary/legendary-pack-on-background@2x.png';
import LegendaryPackBackground3x from 'static/pack/legendary/legendary-pack-on-background@3x.png';

import RewardPack2x from 'static/pack/reward/img-reward-pack-no-background@2x.png';
import RewardPack3x from 'static/pack/reward/img-reward-pack-no-background@3x.png';

import MysteryPack from 'static/pack/mystery/img-mystery-pack-no-background.png';
import MysteryPack2x from 'static/pack/mystery/img-mystery-pack-no-background@2x.png';
import MysteryPack3x from 'static/pack/mystery/img-mystery-pack-no-background@3x.png';
import MysteryPackBackground from 'static/pack/mystery/img-mystery-pack-on-background.png';
import MysteryPackBackground2x from 'static/pack/mystery/img-mystery-pack-on-background@2x.png';
import MysteryPackBackground3x from 'static/pack/mystery/img-mystery-pack-on-background@3x.png';

import Typography from '../Typography';
import { type PackCards, type PackTypes } from './types';

const CardsInfo: FC<{ bold?: boolean }> = ({ children, bold }) => (
  <Typography size="unmedia16" textColor="white" bold={bold}>
    {children}
  </Typography>
);
const CardsInfoSubitem: FC = ({ children }) => (
  <Typography
    size="unmedia16"
    textColor="white"
    lineHeight="1.25em"
    style={{ marginBottom: '-0.25em' }}
  >
    {children}
  </Typography>
);

const getPackCardsInfo = (
  cards?: PackCards,
) => {
  if (!cards) return null;
  const { legendary, common, rare } = cards;
  return (
    <>
      {legendary ? (
        <CardsInfo>
          {i18n.t('wallet.packs.info.legendaryPlusAmount', { count: legendary })}
        </CardsInfo>
      ) : null}
      {rare ? (
        <CardsInfo>
          {i18n.t('wallet.packs.info.rarePlusAmount', { count: rare })}
        </CardsInfo>
      ) : null}
      {common ? (
        <CardsInfo>
          {i18n.t('wallet.packs.info.commonPlusAmount', { count: common })}
        </CardsInfo>
      ) : null}
    </>
  );
};
export const getPackInfo = (
  packType: string,
  packDistribution?: PackDistribution | null,
  packCards? : PackCards,
) => {
  const packData = packDistribution?.[packType] || null;
  const randomCardsTexts = packData?.random_card;
  const randomBlock = randomCardsTexts
    ? (
      <>
        <CardsInfo bold>
          {i18n.t(
            packData?.random_cards === 1
              ? 'wallet.packs.info.randomAmount'
              : 'wallet.packs.info.randomAmount_other', { count: packData?.random_cards },
          )}
        </CardsInfo>
        {randomCardsTexts.map((rText) => (
          <CardsInfoSubitem key={rText}>{rText}</CardsInfoSubitem>
        ))}
      </>
    )
    : null;
  switch (packType) {
    case 'mystery': return (
      <>
        { !!packData?.rare_cards && (
          <CardsInfo>
            {i18n.t('wallet.packs.info.rareAmount', { count: packData?.rare_cards })}
          </CardsInfo>
        )}
        { !!packData?.common_cards && (
          <CardsInfo>
            {i18n.t('wallet.packs.info.commonAmount', { count: packData?.common_cards })}
          </CardsInfo>
        )}
        {randomBlock}
      </>
    );

    default:
    case 'common': return (
      <>
        { !!packDistribution?.mixin?.legendary_to_common_count && (
          <CardsInfo>
              {i18n.t('wallet.packs.info.legendaryChance', {
                chance: (100 * (packDistribution?.mixin.legendary_to_common_count / packData?.total)).toFixed(1),
              })}
          </CardsInfo>
        )}
        { !!packDistribution?.mixin?.rare_to_common_count && (
          <CardsInfo>
            {i18n.t('wallet.packs.info.rareChance', {
              chance: (100 * (packDistribution?.mixin.rare_to_common_count / packData?.total)).toFixed(1),
            })}
          </CardsInfo>
        )}
        { !!packData?.common_cards && (
          <CardsInfo>
            {i18n.t('wallet.packs.info.commonAmount', { count: packData?.common_cards })}
          </CardsInfo>
        )}
        {randomBlock}
      </>
    );

    case 'rare': return (
      <>
        { !!packDistribution?.mixin?.legendary_to_rare_count && (
          <CardsInfo>
            {i18n.t('wallet.packs.info.legendaryChance', {
              chance: (100 * (packDistribution?.mixin.legendary_to_rare_count / packData?.total)).toFixed(1),
            })}
          </CardsInfo>
        )}
        <CardsInfo>{i18n.t('wallet.packs.info.rareAmount', { count: 1 })}</CardsInfo>
        { !!packData?.common_cards && (
          <CardsInfo>
            {i18n.t('wallet.packs.info.commonAmount', { count: packData?.common_cards })}
          </CardsInfo>
        )}
        {randomBlock}
      </>
    );

    case 'legendary':
      return (
        <>
          <CardsInfo>{i18n.t('wallet.packs.info.legendaryAmount_one', { count: packData?.legendary_cars || 1 })}</CardsInfo>
          {
            packDistribution && !!packData?.rare_cards && (
              <CardsInfo>{i18n.t('wallet.packs.info.rareAmount', { count: packData?.rare_cards })}</CardsInfo>
            )
          }

          {packDistribution && !!packData?.common_cards && (
          <CardsInfo>
            {i18n.t('wallet.packs.info.commonAmount', { count: packData?.common_cards })}
          </CardsInfo>
          )}
          {randomBlock}
        </>
      );

    case 'common_plus': return getPackCardsInfo(packCards);

    case 'rare_plus': return getPackCardsInfo(packCards);

    case 'legendary_plus': return getPackCardsInfo(packCards);
  }
};

export const packImages: { [key in PackTypes]: any } = {
  common: {
    withBackground: {
      src: CommonPackBackground,
      srcset: `${CommonPackBackground2x} 2x, ${CommonPackBackground3x} 3x`,
    },
    withoutBackground: {
      src: CommonPack,
      srcset: `${CommonPack2x} 2x, ${CommonPack3x} 3x`,
    },
  },
  rare: {
    withBackground: {
      src: RarePackBackground,
      srcset: `${RarePackBackground2x} 2x, ${RarePackBackground3x} 3x`,
    },
    withoutBackground: {
      src: RarePackBack,
      srcset: `${RarePackBack2x} 2x, ${RarePackBack3x} 3x`,
    },
  },
  legendary: {
    withBackground: {
      src: LegendaryPackBackground,
      srcset: `${LegendaryPackBackground2x} 2x, ${LegendaryPackBackground3x} 3x`,
    },
    withoutBackground: {
      src: LegendaryPack,
      srcset: `${LegendaryPack2x} 2x, ${LegendaryPack3x} 3x`,
    },
  },
  common_plus: {
    withBackground: {
      src: RewardPack3x,
      srcset: `${RewardPack2x} 2x, ${RewardPack3x} 3x`,
    },
    withoutBackground: {
      src: RewardPack3x,
      srcset: `${RewardPack2x} 2x, ${RewardPack3x} 3x`,
    },
  },
  rare_plus: {
    withBackground: {
      src: RewardPack3x,
      srcset: `${RewardPack2x} 2x, ${RewardPack3x} 3x`,
    },
    withoutBackground: {
      src: RewardPack3x,
      srcset: `${RewardPack2x} 2x, ${RewardPack3x} 3x`,
    },
  },
  legendary_plus: {
    withBackground: {
      src: RewardPack3x,
      srcset: `${RewardPack2x} 2x, ${RewardPack3x} 3x`,
    },
    withoutBackground: {
      src: RewardPack3x,
      srcset: `${RewardPack2x} 2x, ${RewardPack3x} 3x`,
    },
  },
  mystery: {
    withBackground: {
      src: MysteryPackBackground,
      srcset: `${MysteryPackBackground2x} 2x, ${MysteryPackBackground3x} 3x`,
    },
    withoutBackground: {
      src: MysteryPack,
      srcset: `${MysteryPack2x} 2x, ${MysteryPack3x} 3x`,
    },
  },
};

export const packMedia: { [key in PackTypes]: any } = {
  common: {
    type: 'image',
    sizes: [
      {
        height: 500,
        width: 500,
        url: CommonPack,
      },
    ],
  },
  rare: {
    type: 'image',
    sizes: [
      {
        height: 500,
        width: 500,
        url: RarePackBack,
      },
    ],
  },
  legendary: {
    type: 'image',
    sizes: [
      {
        height: 500,
        width: 500,
        url: LegendaryPack,
      },
    ],
  },
  // change url for commom_plus, rare_plus, legendary_plus
  common_plus: {
    type: 'image',
    sizes: [
      {
        height: 500,
        width: 500,
        url: CommonPack,
      },
    ],
  },
  rare_plus: {
    type: 'image',
    sizes: [
      {
        height: 500,
        width: 500,
        url: RarePackBack,
      },
    ],
  },
  legendary_plus: {
    type: 'image',
    sizes: [
      {
        height: 500,
        width: 500,
        url: LegendaryPack,
      },
    ],
  },
  mystery: {
    type: 'image',
    sizes: [
      {
        height: 500,
        width: 500,
        url: MysteryPack,
      },
    ],
  },
};
